import AppService from 'App/legacy/AppService';
import type Client from 'App/resources/Model/Client';
import { Feature, hasSoftDeployAccess, isAvailable } from 'App/services/FeatureHelperStandalone';
import { canAddToSegment } from 'App/helpers/segmentAccess';

/**
 * What the contact card's quick-create menu is allowed to offer for this role,
 * before the per-contact permission checks each action does for itself.
 *
 * This is `ContactCtrl.availableFeatures` plus the loose flags the Angular card
 * kept next to it (contact.js:1868-1965), gathered in one place so the action
 * list stays a list of actions.
 */
export type ContactCreateAvailable = {
	activity: boolean;
	todo: boolean;
	appointment: boolean;
	opportunity: boolean;
	order: boolean;
	agreement: boolean;
	document: boolean;
	udo: boolean;
	marketEvent: boolean;
	groupEmail: boolean;
	canResub: boolean;
	relation: boolean;
	uploadFile: boolean;
	esign: boolean;
	exportData: boolean;
	support: boolean;
	projectPlan: boolean;
	segment: boolean;
	/**
	 * The account this contact sits on has been deactivated. Angular swapped the
	 * whole menu for a mostly greyed-out copy in this state; note it keys off the
	 * *account*, not the contact.
	 */
	accountInactive: boolean;
};

export const contactCreateAvailable = (client: Client): ContactCreateAvailable => {
	const self = AppService.getSelf();
	const metadata = AppService.getMetadata();
	const hasActivities = isAvailable(Feature.ACTIVITIES_AND_APPOINTMENTS);
	const hasTodoList = hasSoftDeployAccess('TODO_LIST');
	const hasDocuments = isAvailable(Feature.DOCUMENTS);

	// Angular wrote this as `(feature || softDeploy) && requireAddon ? self.service : self.service || self.crm`,
	// where the ternary swallows the feature check — the item showed for any crm
	// role with neither flag. Same clauses, grouped the way the client card reads
	// them (ClientCardHeader), so the feature actually gates.
	const hasProjectPlan = isAvailable(Feature.PROJECT_PLAN) || hasSoftDeployAccess('PROJECT_PLAN');
	const projectPlanRequiresAddon = hasSoftDeployAccess('SERVICE_MANAGEMENT_REQUIRE_ADDON');

	return {
		// TODO_LIST replaces the activity entry with todo + phone call, and
		// REMOVE_ACTIVITIES takes the activity entry away entirely.
		activity: hasActivities && !(hasSoftDeployAccess('REMOVE_ACTIVITIES') && hasTodoList),
		todo: hasActivities && hasTodoList,
		appointment: hasActivities,
		opportunity: isAvailable(Feature.PIPELINE),
		order: isAvailable(Feature.ORDERS),
		agreement:
			isAvailable(Feature.RECURRING_ORDER) &&
			!!metadata.params.AgreementEnabled &&
			!!AppService.getAccessRights().Agreement,
		document: hasDocuments && !!AppService.getDocumentTemplates('client').length,
		udo: isAvailable(Feature.UDO),
		marketEvent: isAvailable(Feature.MARKETING_EVENTS),
		groupEmail: isAvailable(Feature.GROUP_MAIL) && !!metadata.map.mailActivated,
		canResub: !!self.administrator || !!self.userParams?.mailAdmin,
		relation: isAvailable(Feature.COMPANY_RELATIONS),
		uploadFile: hasDocuments,
		esign: isAvailable(Feature.ESIGN) && !!AppService.getEsignIntegrations().length,
		exportData: isAvailable(Feature.EXPORT_DATA),
		support: isAvailable(Feature.CUSTOMER_SUPPORT),
		projectPlan: hasProjectPlan && (projectPlanRequiresAddon ? !!self.service : !!(self.service || self.crm)),
		segment: canAddToSegment(),
		accountInactive: isAvailable(Feature.INACTIVE_COMPANIES) && !client.active
	};
};
